import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, Select, Table, Checkbox, Upload, Spin } from "antd";
import dayjs from "dayjs";
import {
    ChevronLeft,
    ChevronRight,
    FileText,
    Package,
    ClipboardCheck,
    ClipboardList,
    CalendarDays,
    Save,
    Mail,
    CheckCircle2,
    UploadCloud,
    Trash2,
    Plus,
    Download,
} from "lucide-react";

import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import { modalFieldLabelClass } from "../../components/ui/fieldStyles";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import EmployeeRemarksContent from "./helpers/EmployeeRemarksContent";
import EmployeeAuditContent from "./helpers/EmployeeAuditContent";
import {
    RESIGNATION_PROCESS_URL,
    EMPLOYEE_DESIGNATION,
    EMPLOYEE_PERSONAL_INFO,
    LETTER_TYPE_API,
    ABSCONDING_URL,
} from "../../constants/api";

const DATE_FMT = "YYYY-MM-DD";
const APPROVAL_STATUS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
];
const LETTER_STATUS = [
    { value: "provided", label: "Provided" },
    { value: "not_provided", label: "Not Provided" },
];
const SETTLEMENT_STATUS = [
    { value: "cleared", label: "Cleared" },
    { value: "partially_cleared", label: "Partially Cleared" },
    { value: "not_cleared", label: "Not Cleared" },
    { value: "other", label: "Other" },
];

const normalizeLetterStatus = (status) => {
    const legacy = {
        pending: "not_provided",
        issued: "provided",
        received: "provided",
    };
    return legacy[status] || status;
};

const normalizeSettlementStatus = (status) => {
    const legacy = {
        pending: "not_cleared",
        rejected: "not_cleared",
    };
    return legacy[status] || status;
};

const SectionTitle = ({ children }) => (
    <h3 className="text-sm font-semibold text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-3 mt-2">
        {children}
    </h3>
);

const FieldLabel = ({ children, required }) => (
    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
        {children} {required && <span className="text-red-500">*</span>}
    </label>
);

const ReadOnlyField = ({ label, value }) => (
    <div className="space-y-1">
        <FieldLabel>{label}</FieldLabel>
        <div className="px-3 py-2 rounded-md text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 min-h-10">
            {value || "-"}
        </div>
    </div>
);

const blankLetterRow = () => ({
    letter_type: "",
    processed_by: "",
    approved_by: "",
    letter_provided_date: "",
    letter_status: "",
    is_send_mail: false,
    uploaded_documents: [],
    document_comments: "",
});

const ResignationProcess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetch, update } = useApiMethods();

    const { resignationId, stepKey = 0 } = location.state || {};

    const [currentStep, setCurrentStep] = useState(stepKey);
    const [loading, setLoading] = useState(false);
    const [screenLoader, setScreenLoader] = useState(true);
    const [saving, setSaving] = useState(false);

    // Resignation data from API
    const [resignationData, setResignationData] = useState(null);
    const [employeeDetails, setEmployeeDetails] = useState(null);
    const [approverDetails, setApproverDetails] = useState(null);
    const [finalApproverDetails, setFinalApproverDetails] = useState(null);
    const [interviewDetails, setInterviewDetails] = useState(null);
    const [assetDetailsList, setAssetDetailsList] = useState([]);
    const [recentHandover, setRecentHandover] = useState(null);

    // Assets handover state
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [handoverForm, setHandoverForm] = useState({
        designation: undefined,
        handover_to: undefined,
        handover_comments: "",
        approved_date: undefined,
        approved_by: undefined,
        approved_comments: "",
    });

    // Final step state
    const [finalForm, setFinalForm] = useState({
        InterviewedBy: undefined,
        Exit_Interview_date: undefined,
        Exit_interview_status: undefined,
        Exit_interview_comments: "",
        Exit_verified_by: undefined,
        Exit_verified_date: undefined,
        verified_status: undefined,
        verified_comments: "",
        final_settlement_date: dayjs(),
        final_settlement_status: undefined,
        final_settlement_comments: "",
    });
    const [letterRows, setLetterRows] = useState([blankLetterRow()]);
    const [clearanceItems, setClearanceItems] = useState([]);
    const [waiveDraft, setWaiveDraft] = useState({});
    const [wavingId, setWavingId] = useState(null);
    const [payrollHold, setPayrollHold] = useState(false);
    const [lastPayroll, setLastPayroll] = useState(null);
    const [attendance, setAttendance] = useState({ days: [], summary: {} });
    const [salaryObligations, setSalaryObligations] = useState({ items: [] });

    // Dropdowns
    const [designationList, setDesignationList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [letterTypeList, setLetterTypeList] = useState([]);

    // `fetch` from useApiMethods() is a fresh function on every render. Hold it in a ref
    // so effects keyed on `resignationId` don't loop because of a changing function identity.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const isProcessFinished = !!resignationData?.is_process_finished;
    const isAbscond = resignationData?.origin === "absconding";
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelling, setCancelling] = useState(false);

    const fetchResignationDetails = useCallback(async () => {
        if (!resignationId) return;
        setScreenLoader(true);
        try {
            const apiData = await fetchRef.current(RESIGNATION_PROCESS_URL, { id: resignationId });
            if (apiData?.status === "success" && apiData.data?.result?.[0]) {
                const data = apiData.data.result[0];
                setResignationData(data);
                setEmployeeDetails(data.employee_details || null);
                setApproverDetails(data.approver || null);
                setFinalApproverDetails(data.interview_details || null);
                setInterviewDetails(data.interview_details || null);
                setAssetDetailsList(data.asset_details || []);
                setRecentHandover(data.recent_asset_handover || null);
                setClearanceItems(data.clearance_items || []);
                setPayrollHold(!!data.is_payroll_hold);
                setLastPayroll(data.last_payroll || null);
                setAttendance(data.attendance || { days: [], summary: {} });
                setSalaryObligations(data.salary_obligations || { items: [] });

                // Prefill handover form from recent handover
                const rh = data.recent_asset_handover;
                if (rh) {
                    setHandoverForm({
                        designation: rh.designation || undefined,
                        handover_to: rh.handover_to || undefined,
                        handover_comments: rh.comments || "",
                        approved_date: rh.approved_date ? dayjs(rh.approved_date) : undefined,
                        approved_by: rh.approved_by || undefined,
                        approved_comments: rh.approved_comments || "",
                    });
                }

                // Prefill final step form from interview details
                const fd = data.interview_details;
                if (fd) {
                    setFinalForm({
                        InterviewedBy: fd.interview_by || undefined,
                        Exit_Interview_date: fd.interview_date ? dayjs(fd.interview_date) : undefined,
                        Exit_interview_status: fd.interview_status || undefined,
                        Exit_interview_comments: fd.interview_comments || "",
                        Exit_verified_by: fd.verified_by || undefined,
                        Exit_verified_date: fd.verified_date ? dayjs(fd.verified_date) : undefined,
                        verified_status: fd.verified_status || undefined,
                        verified_comments: fd.verified_comments || "",
                        final_settlement_date: fd.final_settlement_date ? dayjs(fd.final_settlement_date) : dayjs(),
                        final_settlement_status: normalizeSettlementStatus(fd.final_settlement_status) || undefined,
                        final_settlement_comments: fd.final_settlement_comments || "",
                    });
                }

                if (data.resignation_docs?.length > 0) {
                    setLetterRows(data.resignation_docs.map((d) => ({
                        ...blankLetterRow(),
                        ...d,
                        letter_status: normalizeLetterStatus(d.letter_status),
                        letter_provided_date: d.letter_provided_date ? dayjs(d.letter_provided_date) : "",
                        uploaded_documents: typeof d.uploaded_documents === "string" && d.uploaded_documents
                            ? [{ name: d.uploaded_documents.split("/").pop(), url: d.uploaded_documents }]
                            : [],
                    })));
                }
            } else {
                ToastMsg("error", apiData?.message || "Could not load resignation details");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setScreenLoader(false);
        }
    }, [resignationId]);

    const fetchDropdowns = useCallback(async () => {
        try {
            const [desg, emps, letters] = await Promise.all([
                fetchRef.current(EMPLOYEE_DESIGNATION, { drop_down: "True" }),
                fetchRef.current(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
                fetchRef.current(LETTER_TYPE_API, { drop_down: "True" }),
            ]);

            if (desg?.status === "success") setDesignationList(desg.data || []);
            if (emps?.status === "success") {
                setEmployeeList((emps.data || []).filter((e) => e.is_active));
            }
            if (letters?.status === "success") {
                setLetterTypeList((letters.data || []).filter((l) => l.process_type_name === "off_bording"));
            }
        } catch (error) {
            ToastMsg("error", error.message);
        }
    }, []);

    useEffect(() => {
        if (!resignationId) {
            ToastMsg("error", "No resignation selected");
            navigate("/employee/resignationrequest");
            return;
        }
        fetchDropdowns();
        fetchResignationDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resignationId]);

    const filteredHandoverEmployees = useMemo(() => {
        if (!handoverForm.designation) return employeeList;
        return employeeList.filter((e) =>
            (e.designation_id || []).some((d) => d.id === handoverForm.designation)
        );
    }, [employeeList, handoverForm.designation]);

    const empCardDetails = useMemo(() => {
        if (!employeeDetails) return null;
        return {
            title: employeeDetails.title || "",
            first_name: employeeDetails.employee_name || "",
            last_name: "",
            designation_id: {
                id: employeeDetails.designation?.id || 0,
                designation_name: employeeDetails.designation?.name || "",
            },
            id: employeeDetails.employee_id || "-",
            profile: employeeDetails.profile,
        };
    }, [employeeDetails]);

    /* ---------------- Step 2: Save Assets Handover ---------------- */
    const saveAssetsHandover = async () => {
        if (selectedAssetIds.length === 0) {
            // Nothing to save — skip to next step
            return true;
        }

        if (!handoverForm.handover_to || !handoverForm.approved_by || !handoverForm.approved_date) {
            ToastMsg("error", "Please fill Handover To, Approved By and Approved Date");
            return false;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append("stage", "2");
            formData.append("employee_id", employeeDetails?.employee_id || "");
            formData.append("resignation_id", resignationData?.id || "");
            formData.append("handover_to", handoverForm.handover_to || "");
            formData.append("return_date", dayjs().format(DATE_FMT));
            formData.append("approved_date", dayjs(handoverForm.approved_date).format(DATE_FMT));
            formData.append("comments", handoverForm.handover_comments || "");
            formData.append("approved_by", handoverForm.approved_by || "");
            formData.append("approved_comments", handoverForm.approved_comments || "");
            formData.append(
                "assets",
                JSON.stringify(selectedAssetIds.map((id) => ({ id, status: true })))
            );

            const response = await update(RESIGNATION_PROCESS_URL, formData, true);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Assets handover saved");
                setSelectedAssetIds([]);
                await fetchResignationDetails();
                return true;
            }
            ToastMsg("error", response?.message || "Failed to save assets handover");
            return false;
        } catch (error) {
            ToastMsg("error", error.message);
            return false;
        } finally {
            setSaving(false);
        }
    };

    /* ---------------- Step 3: Save Final Step ---------------- */
    const saveFinalStep = async ({ sendMail = false, finishProcess = false } = {}) => {
        const required = isAbscond
            ? [
                ["final_settlement_status", "Final Settlement Status"],
                ["final_settlement_comments", "Final Settlement Comments"],
            ]
            : [
                ["InterviewedBy", "Interviewed By"],
                ["Exit_Interview_date", "Exit Interview Date"],
                ["Exit_interview_status", "Exit Interview Status"],
                ["Exit_interview_comments", "Exit Interview Comments"],
                ["Exit_verified_by", "Verified By"],
                ["Exit_verified_date", "Verified Date"],
                ["verified_status", "Verification Status"],
                ["verified_comments", "Verification Comments"],
                ["final_settlement_status", "Final Settlement Status"],
                ["final_settlement_comments", "Final Settlement Comments"],
            ];
        for (const [key, label] of required) {
            if (!finalForm[key] || (typeof finalForm[key] === "string" && !finalForm[key].trim())) {
                ToastMsg("error", `${label} is required`);
                return false;
            }
        }

        // Validate letter rows: filter blanks; require at least one row with data
        const validRows = letterRows.filter((row) =>
            row.letter_type || row.processed_by || row.approved_by || row.letter_status ||
            row.document_comments || (row.uploaded_documents && row.uploaded_documents.length > 0)
        );
        for (const [index, row] of validRows.entries()) {
            const missing = ["letter_type", "processed_by", "approved_by", "letter_provided_date", "letter_status", "document_comments"]
                .find((k) => !row[k]);
            if (missing) {
                ToastMsg("error", `Document row ${index + 1}: ${missing.replace(/_/g, " ")} is required`);
                return false;
            }
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append("stage", "3");
            formData.append("resignation_id", resignationData?.id || "");
            formData.append("employee_id", employeeDetails?.employee_id || "");
            if (resignationData?.interview_id) {
                formData.append("interview_id", resignationData.interview_id);
            }

            // Stage-3 fields are flat — the Django view reads each via request.data.get(...).
            formData.append("interview_by", finalForm.InterviewedBy || "");
            formData.append("interview_date", finalForm.Exit_Interview_date ? dayjs(finalForm.Exit_Interview_date).format(DATE_FMT) : "");
            formData.append("interview_status", finalForm.Exit_interview_status || "");
            formData.append("interview_comments", finalForm.Exit_interview_comments || "");
            formData.append("verified_by", finalForm.Exit_verified_by || "");
            formData.append("verified_date", finalForm.Exit_verified_date ? dayjs(finalForm.Exit_verified_date).format(DATE_FMT) : "");
            formData.append("verified_status", finalForm.verified_status || "");
            formData.append("verified_comments", finalForm.verified_comments || "");
            formData.append("final_settlement_date", finalForm.final_settlement_date ? dayjs(finalForm.final_settlement_date).format(DATE_FMT) : "");
            formData.append("final_settlement_status", finalForm.final_settlement_status || "");
            formData.append("final_settlement_comments", finalForm.final_settlement_comments || "");

            const docPayload = validRows.map((row, index) => {
                const fileEntry = row.uploaded_documents?.[0];
                const isExistingUrl = fileEntry && (fileEntry.url || typeof fileEntry === "string");
                if (fileEntry && fileEntry.originFileObj) {
                    formData.append(`document__${index}`, fileEntry.originFileObj);
                }
                const doc = {
                    letter_type: row.letter_type,
                    processed_by: row.processed_by,
                    approved_by: row.approved_by,
                    letter_provided_date: row.letter_provided_date ? dayjs(row.letter_provided_date).format(DATE_FMT) : "",
                    letter_status: row.letter_status,
                    is_send_mail: !!row.is_send_mail,
                    document_comments: row.document_comments,
                    uploaded_documents: isExistingUrl
                        ? (typeof fileEntry === "string" ? fileEntry : fileEntry.url)
                        : "",
                };
                if (row.id) doc.id = row.id;
                return doc;
            });
            formData.append("resignation_documents", JSON.stringify(docPayload));

            formData.append("is_process_finished", finishProcess ? "true" : "false");
            formData.append("send_mail", sendMail ? "true" : "false");

            const response = await update(RESIGNATION_PROCESS_URL, formData, true);
            if (response?.status === "success") {
                ToastMsg("success", response.message || (isAbscond ? "Abscond process saved successfully" : "Final step saved"));
                if (finishProcess) {
                    navigate(isAbscond ? "/employee/absconding" : "/employee/resignationrequest");
                } else {
                    await fetchResignationDetails();
                }
                return true;
            }
            ToastMsg("error", response?.message || "Failed to save final step");
            return false;
        } catch (error) {
            ToastMsg("error", error.message);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const savePayrollHold = async (nextHold) => {
        if (isProcessFinished) return true;
        const forced = resignationData?.origin === "absconding";
        const hold = forced ? true : (nextHold !== undefined ? nextHold : payrollHold);
        setSaving(true);
        try {
            const response = await update(RESIGNATION_PROCESS_URL, {
                resignation_id: resignationData?.id,
                action: "payroll_hold",
                is_payroll_hold: hold,
            });
            if (response?.status === "success") {
                const applied = response.data?.is_payroll_hold ?? hold;
                setPayrollHold(!!applied);
                if (response.data?.salary_obligations) {
                    setSalaryObligations(response.data.salary_obligations);
                }
                return true;
            }
            ToastMsg("error", response?.message || "Could not update payroll hold");
            return false;
        } catch (error) {
            ToastMsg("error", error.message);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleWaiveItem = async (item) => {
        const comment = (waiveDraft[item.id] || "").trim();
        if (!comment) {
            ToastMsg("error", "Waive comment is required");
            return;
        }
        setWavingId(item.id);
        try {
            const response = await update(RESIGNATION_PROCESS_URL, {
                resignation_id: resignationData?.id,
                action: "waive",
                item_id: item.id,
                waive_comment: comment,
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Item waived");
                if (response.data?.clearance_items) {
                    setClearanceItems(response.data.clearance_items);
                } else {
                    await fetchResignationDetails();
                }
                setWaiveDraft((prev) => ({ ...prev, [item.id]: "" }));
            } else {
                ToastMsg("error", response?.message || "Could not waive item");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setWavingId(null);
        }
    };

    /* ---------------- Stepper ---------------- */
    const steps = [
        { title: isAbscond ? "Abscond Submission" : "Resignation Submission", icon: FileText },
        { title: "Attendance", icon: CalendarDays },
        { title: "Audit", icon: ClipboardList },
        { title: "Assets Handover", icon: Package },
        { title: "Final Verification", icon: ClipboardCheck },
    ];

    const handleNext = async () => {
        if (currentStep === 1) {
            const ok = await savePayrollHold();
            if (!ok) return;
        }
        if (currentStep === 3) {
            const ok = await saveAssetsHandover();
            if (!ok) return;
        }
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep === 0) {
            navigate(isAbscond ? "/employee/absconding" : "/employee/resignationrequest");
            return;
        }
        setCurrentStep(currentStep - 1);
    };

    const cancelAbscondCase = async () => {
        if (!cancelReason.trim()) {
            ToastMsg("error", "Cancel reason is required");
            return;
        }
        setCancelling(true);
        try {
            const response = await update(ABSCONDING_URL, {
                id: resignationData?.id,
                action: "cancel",
                reason: cancelReason.trim(),
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Abscond case cancelled");
                setCancelOpen(false);
                navigate("/employee/absconding");
                return;
            }
            ToastMsg("error", response?.message || "Could not cancel abscond case");
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setCancelling(false);
        }
    };

    /* ---------------- Renderers ---------------- */
    const renderSubmission = () => (
        <div className="space-y-4">
            <SectionTitle>Employee Information</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ReadOnlyField label="Employee Name" value={employeeDetails?.employee_name} />
                <ReadOnlyField label="Employee ID" value={employeeDetails?.employee_id} />
                <ReadOnlyField label="Department" value={employeeDetails?.department?.name} />
                <ReadOnlyField label="Designation" value={employeeDetails?.designation?.name} />
                <ReadOnlyField
                    label="Requested Date"
                    value={resignationData?.request_date ? dayjs(resignationData.request_date).format("DD-MM-YYYY") : "-"}
                />
            </div>
            <div className="space-y-2">
                <FieldLabel>Reason for Resignation</FieldLabel>
                <div className="px-3 py-2 rounded-md text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 min-h-20 whitespace-pre-wrap">
                    {resignationData?.reason || "-"}
                </div>
            </div>

            <SectionTitle>Approver Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ReadOnlyField label="Approver" value={approverDetails?.approved_by_name} />
                <ReadOnlyField
                    label="Approval Date"
                    value={approverDetails?.approval_date ? dayjs(approverDetails.approval_date.split("T")[0]).format("DD-MM-YYYY") : "-"}
                />
                <ReadOnlyField label="Status" value={approverDetails?.approval_status} />
                <ReadOnlyField label="Notice Period" value={approverDetails?.notice_period} />
                <ReadOnlyField
                    label="Last Working Day"
                    value={approverDetails?.last_working_day ? dayjs(approverDetails.last_working_day).format("DD-MM-YYYY") : "-"}
                />
            </div>
            <div className="space-y-2">
                <FieldLabel>Approval Comments</FieldLabel>
                <div className="px-3 py-2 rounded-md text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 min-h-16 whitespace-pre-wrap">
                    {approverDetails?.approval_comments || "-"}
                </div>
            </div>

            <SectionTitle>Final Approver Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ReadOnlyField label="Final Approver" value={finalApproverDetails?.approved_by_name || resignationData?.final_approver?.approved_by_name} />
                <ReadOnlyField
                    label="Final Approval Date"
                    value={
                        finalApproverDetails?.approval_date
                            ? dayjs(finalApproverDetails.approval_date.split("T")[0]).format("DD-MM-YYYY")
                            : resignationData?.final_approver?.approval_date
                                ? dayjs(resignationData.final_approver.approval_date.split("T")[0]).format("DD-MM-YYYY")
                                : "-"
                    }
                />
                <ReadOnlyField
                    label="Status"
                    value={finalApproverDetails?.approval_status || resignationData?.final_approver?.approval_status}
                />
                <ReadOnlyField
                    label="Notice Period"
                    value={finalApproverDetails?.notice_period || resignationData?.final_approver?.notice_period}
                />
                <ReadOnlyField
                    label="Last Working Day"
                    value={
                        finalApproverDetails?.last_working_day
                            ? dayjs(finalApproverDetails.last_working_day).format("DD-MM-YYYY")
                            : resignationData?.final_approver?.last_working_day
                                ? dayjs(resignationData.final_approver.last_working_day).format("DD-MM-YYYY")
                                : "-"
                    }
                />
            </div>
            <div className="space-y-2">
                <FieldLabel>Final Approval Comments</FieldLabel>
                <div className="px-3 py-2 rounded-md text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 min-h-16 whitespace-pre-wrap">
                    {finalApproverDetails?.approval_comments || resignationData?.final_approver?.approval_comments || "-"}
                </div>
            </div>
        </div>
    );

    const money = (value) => (value === null || value === undefined || value === "" ? "-" : value);
    const employeePk = resignationData?.employee_pk || employeeDetails?.id;
    const holdForced = resignationData?.origin === "absconding";

    const renderAttendance = () => (
        <div className="space-y-4">
            {resignationData?.origin === "absconding" && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                    Abscond case. Payroll hold is forced on. Login is disabled.
                </div>
            )}
            <SectionTitle>Last payroll run</SectionTitle>
            {lastPayroll ? (
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <ReadOnlyField label="Period" value={lastPayroll.period_name || `${lastPayroll.month_name || ""} ${lastPayroll.year || ""}`} />
                        <ReadOnlyField
                            label="From / To"
                            value={`${lastPayroll.from_date ? dayjs(lastPayroll.from_date).format("DD-MM-YYYY") : "-"} – ${lastPayroll.to_date ? dayjs(lastPayroll.to_date).format("DD-MM-YYYY") : "-"}`}
                        />
                        <ReadOnlyField label="Status" value={lastPayroll.is_publish ? "Published" : lastPayroll.status} />
                        <ReadOnlyField label="Earnings" value={money(lastPayroll.earnings)} />
                        <ReadOnlyField label="Deductions" value={money(lastPayroll.deductions)} />
                        <ReadOnlyField label="Net pay" value={money(lastPayroll.net_pay)} />
                        <ReadOnlyField label="LOP days" value={money(lastPayroll.lop_days)} />
                        <ReadOnlyField label="LOP amount" value={money(lastPayroll.lop_amount)} />
                    </div>
                    {lastPayroll.has_payslip === false && (
                        <p className="text-xs text-gray-500">No payslip for this employee in this run.</p>
                    )}
                </div>
            ) : (
                <p className="text-sm text-gray-500">No payroll run found for this employee.</p>
            )}

            <SectionTitle>Attendance details</SectionTitle>
            <p className="text-xs text-gray-500">
                {attendance?.from_date && attendance?.to_date
                    ? `${dayjs(attendance.from_date).format("DD-MM-YYYY")} to ${dayjs(attendance.to_date).format("DD-MM-YYYY")}`
                    : "Last payroll period, or the last 30 days up to today."}
            </p>
            {attendance?.summary && Object.keys(attendance.summary).length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                    {Object.entries(attendance.summary).map(([status, count]) => (
                        <span key={status} className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                            {status}: {count}
                        </span>
                    ))}
                </div>
            )}
            <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={attendance?.days || []}
                    locale={{ emptyText: "No attendance rows in this period" }}
                    columns={[
                        { title: "Date", dataIndex: "date", key: "date", render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-") },
                        { title: "Status", dataIndex: "status", key: "status" },
                        { title: "Shift", dataIndex: "shift", key: "shift", render: (v) => v || "-" },
                        { title: "Check in", dataIndex: "check_in", key: "check_in", render: (v) => v || "-" },
                        { title: "Check out", dataIndex: "check_out", key: "check_out", render: (v) => v || "-" },
                        { title: "Hours", dataIndex: "working_hrs", key: "working_hrs", render: (v) => v || "-" },
                        { title: "OT", dataIndex: "over_time", key: "over_time", render: (v) => v || "-" },
                    ]}
                />
            </div>

            <SectionTitle>Payroll hold</SectionTitle>
            {(salaryObligations?.items || []).length > 0 && (
                <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 mb-2">
                    {salaryObligations.items.map((item) => (
                        <li key={`${item.item_code}-${item.source_id}`}>
                            {item.label}{item.amount != null ? ` · ${item.amount}` : ""}
                        </li>
                    ))}
                </ul>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                    type="checkbox"
                    checked={holdForced ? true : payrollHold}
                    disabled={isProcessFinished || holdForced}
                    onChange={(e) => {
                        const next = e.target.checked;
                        setPayrollHold(next);
                        savePayrollHold(next);
                    }}
                />
                Put payroll on hold
            </label>
            <p className="text-xs text-gray-500">
                This writes the same flag as Payroll → Payroll Hold. Changing it here updates that list for active employees.
            </p>

            <SectionTitle>Resignation remarks</SectionTitle>
            {employeePk ? (
                <EmployeeRemarksContent
                    employeeID={employeePk}
                    compact
                    heading="Employee remarks"
                    addButtonLabel="Add resignation remark"
                    dialogTitle="Add resignation remark"
                    allowAdd
                />
            ) : (
                <p className="text-sm text-gray-500">Employee not loaded.</p>
            )}
        </div>
    );

    const renderAudit = () => (
        <div className="space-y-4">
            <SectionTitle>Employee audit</SectionTitle>
            <p className="text-xs text-gray-500 mb-2">
                Same audit trail as Employee List → employee details → Audit.
            </p>
            {employeePk ? (
                <EmployeeAuditContent employeeID={employeePk} embedded />
            ) : (
                <p className="text-sm text-gray-500">Employee not loaded.</p>
            )}
        </div>
    );

    const renderClearancePanel = () => (
        <div className="mb-4">
            <SectionTitle>Pending obligations</SectionTitle>
            <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={clearanceItems}
                    locale={{ emptyText: "No obligations recorded yet" }}
                    columns={[
                        { title: "Item", dataIndex: "label", key: "label" },
                        { title: "Owner", dataIndex: "owner_name", key: "owner_name", render: (v) => v || "-" },
                        { title: "Status", dataIndex: "status", key: "status" },
                        {
                            title: "Waive",
                            key: "waive",
                            width: 280,
                            render: (_, item) => {
                                if (item.status !== "pending") return item.waive_comment || "-";
                                if (isProcessFinished) return "-";
                                return (
                                    <div className="flex items-start gap-2 py-1">
                                        <Textarea
                                            rows={2}
                                            value={waiveDraft[item.id] || ""}
                                            onChange={(e) => setWaiveDraft((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                            placeholder="Comment required"
                                        />
                                        <Button size="sm" variant="outline" disabled={wavingId === item.id} onClick={() => handleWaiveItem(item)}>
                                            Waive
                                        </Button>
                                    </div>
                                );
                            },
                        },
                    ]}
                />
            </div>
        </div>
    );

    const assetsColumns = [
        { title: "Asset ID", dataIndex: "asset_id", key: "asset_id" },
        { title: "Asset Name", dataIndex: "asset_name", key: "asset_name" },
        { title: "Issued Date", dataIndex: "issued_date", key: "issued_date" },
        { title: "Issued By", dataIndex: "issued_by", key: "issued_by" },
    ];

    const assetRowSelection = {
        selectedRowKeys: selectedAssetIds,
        onChange: (keys) => setSelectedAssetIds(keys),
        getCheckboxProps: (record) => ({ disabled: record.return_date !== null }),
    };

    const isHandoverMandatory = selectedAssetIds.length > 0;

    const renderAssetsHandover = () => (
        <div className="space-y-4">
            {renderClearancePanel()}
            <SectionTitle>Select Assets to Return</SectionTitle>
            <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                <Table
                    rowKey="id"
                    columns={assetsColumns}
                    dataSource={assetDetailsList}
                    rowSelection={assetRowSelection}
                    pagination={false}
                    size="small"
                    locale={{ emptyText: "No assets assigned" }}
                />
            </div>

            <SectionTitle>Handover Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                    <FieldLabel>Return Date</FieldLabel>
                    <DatePicker value={dayjs()} disabled format="DD-MM-YYYY" className="w-full" size="large" />
                </div>
                <div className="space-y-2">
                    <FieldLabel required={isHandoverMandatory}>Designation</FieldLabel>
                    <Select
                        value={handoverForm.designation}
                        onChange={(v) => setHandoverForm((p) => ({ ...p, designation: v, handover_to: undefined }))}
                        placeholder="Select designation"
                        className="w-full"
                        size="large"
                        disabled={!isHandoverMandatory || isProcessFinished}
                        allowClear
                    >
                        {designationList.map((opt) => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {opt.designation_name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className="space-y-2">
                    <FieldLabel required={isHandoverMandatory}>Handover To</FieldLabel>
                    <Select
                        value={handoverForm.handover_to}
                        onChange={(v) => setHandoverForm((p) => ({ ...p, handover_to: v }))}
                        placeholder="Select employee"
                        className="w-full"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        disabled={!isHandoverMandatory || isProcessFinished}
                        allowClear
                    >
                        {filteredHandoverEmployees.map((opt) => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {`${opt.first_name || ""} ${opt.last_name || ""} ${opt.employee_id ? "- " + opt.employee_id : ""}`}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <FieldLabel required={isHandoverMandatory}>Handover Comments</FieldLabel>
                <Textarea
                    value={handoverForm.handover_comments}
                    onChange={(e) => setHandoverForm((p) => ({ ...p, handover_comments: e.target.value }))}
                    rows={3}
                    showCharCount
                    maxLength={150}
                    disabled={!isHandoverMandatory || isProcessFinished}
                />
            </div>

            <SectionTitle>Approver Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <FieldLabel required={isHandoverMandatory}>Approved Date</FieldLabel>
                    <DatePicker
                        value={handoverForm.approved_date}
                        onChange={(d) => setHandoverForm((p) => ({ ...p, approved_date: d }))}
                        format="DD-MM-YYYY"
                        className="w-full"
                        size="large"
                        disabled={!isHandoverMandatory || isProcessFinished}
                    />
                </div>
                <div className="space-y-2">
                    <FieldLabel required={isHandoverMandatory}>Approved By</FieldLabel>
                    <Select
                        value={handoverForm.approved_by}
                        onChange={(v) => setHandoverForm((p) => ({ ...p, approved_by: v }))}
                        placeholder="Select approver"
                        className="w-full"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        disabled={!isHandoverMandatory || isProcessFinished}
                        allowClear
                    >
                        {employeeList.map((opt) => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {`${opt.first_name || ""} ${opt.last_name || ""} ${opt.employee_id ? "- " + opt.employee_id : ""}`}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <FieldLabel required={isHandoverMandatory}>Approved Comments</FieldLabel>
                <Textarea
                    value={handoverForm.approved_comments}
                    onChange={(e) => setHandoverForm((p) => ({ ...p, approved_comments: e.target.value }))}
                    rows={3}
                    showCharCount
                    maxLength={150}
                    disabled={!isHandoverMandatory || isProcessFinished}
                />
            </div>
        </div>
    );

    const updateLetterRow = (index, field, value) => {
        setLetterRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const addLetterRow = () => {
        const last = letterRows[letterRows.length - 1];
        const filled = last && last.letter_type && last.processed_by && last.approved_by &&
            last.letter_provided_date && last.letter_status && last.document_comments;
        if (!filled) {
            ToastMsg("error", "Please fill the previous document row before adding a new one");
            return;
        }
        const dup = letterRows.find((r) => r.letter_type && r.letter_type === last.letter_type);
        if (dup && dup !== last) {
            ToastMsg("error", "Same Letter Type already added");
            return;
        }
        setLetterRows((rows) => [...rows, blankLetterRow()]);
    };

    const removeLetterRow = (index) => {
        setLetterRows((rows) => rows.filter((_, i) => i !== index));
    };

    const renderFinalStep = () => (
        <div className="space-y-4">
            <SectionTitle>Exit Interview</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                    <FieldLabel required>Interviewed By</FieldLabel>
                    <Select
                        value={finalForm.InterviewedBy}
                        onChange={(v) => setFinalForm((p) => ({ ...p, InterviewedBy: v }))}
                        placeholder="Select interviewer"
                        className="w-full"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        disabled={isProcessFinished}
                        allowClear
                    >
                        {employeeList.map((opt) => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {`${opt.first_name || ""} ${opt.last_name || ""} ${opt.employee_id ? "- " + opt.employee_id : ""}`}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className="space-y-2">
                    <FieldLabel required>Exit Interview Date</FieldLabel>
                    <DatePicker
                        value={finalForm.Exit_Interview_date}
                        onChange={(d) => setFinalForm((p) => ({ ...p, Exit_Interview_date: d }))}
                        format="DD-MM-YYYY"
                        className="w-full"
                        size="large"
                        disabled={isProcessFinished}
                    />
                </div>
                <div className="space-y-2">
                    <FieldLabel required>Status</FieldLabel>
                    <Select
                        value={finalForm.Exit_interview_status}
                        onChange={(v) => setFinalForm((p) => ({ ...p, Exit_interview_status: v }))}
                        placeholder="Select status"
                        className="w-full"
                        size="large"
                        disabled={isProcessFinished}
                    >
                        {APPROVAL_STATUS.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <FieldLabel required>Exit Interview Comments</FieldLabel>
                <Textarea
                    value={finalForm.Exit_interview_comments}
                    onChange={(e) => setFinalForm((p) => ({ ...p, Exit_interview_comments: e.target.value }))}
                    rows={3}
                    showCharCount
                    maxLength={150}
                    disabled={isProcessFinished}
                />
            </div>

            <SectionTitle>Exit Verification</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                    <FieldLabel required>Verified By</FieldLabel>
                    <Select
                        value={finalForm.Exit_verified_by}
                        onChange={(v) => setFinalForm((p) => ({ ...p, Exit_verified_by: v }))}
                        placeholder="Select verifier"
                        className="w-full"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        disabled={isProcessFinished}
                        allowClear
                    >
                        {employeeList.map((opt) => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {`${opt.first_name || ""} ${opt.last_name || ""} ${opt.employee_id ? "- " + opt.employee_id : ""}`}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className="space-y-2">
                    <FieldLabel required>Verified Date</FieldLabel>
                    <DatePicker
                        value={finalForm.Exit_verified_date}
                        onChange={(d) => setFinalForm((p) => ({ ...p, Exit_verified_date: d }))}
                        format="DD-MM-YYYY"
                        className="w-full"
                        size="large"
                        disabled={isProcessFinished}
                    />
                </div>
                <div className="space-y-2">
                    <FieldLabel required>Status</FieldLabel>
                    <Select
                        value={finalForm.verified_status}
                        onChange={(v) => setFinalForm((p) => ({ ...p, verified_status: v }))}
                        placeholder="Select status"
                        className="w-full"
                        size="large"
                        disabled={isProcessFinished}
                    >
                        {APPROVAL_STATUS.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <FieldLabel required>Verification Comments</FieldLabel>
                <Textarea
                    value={finalForm.verified_comments}
                    onChange={(e) => setFinalForm((p) => ({ ...p, verified_comments: e.target.value }))}
                    rows={3}
                    showCharCount
                    maxLength={150}
                    disabled={isProcessFinished}
                />
            </div>

            <SectionTitle>Resignation Documents</SectionTitle>
            <div className="space-y-4">
                {letterRows.map((row, index) => {
                    const fileEntry = row.uploaded_documents?.[0];
                    const fileName = fileEntry?.name || (typeof fileEntry === "string" ? fileEntry.split("/").pop() : "");
                    const fileUrl = fileEntry?.url || (typeof fileEntry === "string" ? fileEntry : null);
                    return (
                        <div key={index} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/40">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Document #{index + 1}</h4>
                                {letterRows.length > 1 && (
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        onClick={() => removeLetterRow(index)}
                                        disabled={isProcessFinished}
                                        className="h-7 px-2 text-red-600 bg-red-50 border border-red-200 hover:bg-red-100"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="space-y-1">
                                    <FieldLabel required>Letter Type</FieldLabel>
                                    <Select
                                        value={row.letter_type || undefined}
                                        onChange={(v) => updateLetterRow(index, "letter_type", v)}
                                        placeholder="Select letter type"
                                        className="w-full"
                                        disabled={isProcessFinished}
                                        allowClear
                                    >
                                        {letterTypeList.map((opt) => (
                                            <Select.Option key={opt.id} value={opt.id}>
                                                {opt.letter_type_name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <FieldLabel required>Processed By</FieldLabel>
                                    <Select
                                        value={row.processed_by || undefined}
                                        onChange={(v) => updateLetterRow(index, "processed_by", v)}
                                        placeholder="Select employee"
                                        className="w-full"
                                        showSearch
                                        optionFilterProp="children"
                                        disabled={isProcessFinished}
                                        allowClear
                                    >
                                        {employeeList.map((opt) => (
                                            <Select.Option key={opt.id} value={opt.id}>
                                                {`${opt.first_name || ""} ${opt.last_name || ""}`}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <FieldLabel required>Approved By</FieldLabel>
                                    <Select
                                        value={row.approved_by || undefined}
                                        onChange={(v) => updateLetterRow(index, "approved_by", v)}
                                        placeholder="Select approver"
                                        className="w-full"
                                        showSearch
                                        optionFilterProp="children"
                                        disabled={isProcessFinished}
                                        allowClear
                                    >
                                        {employeeList.map((opt) => (
                                            <Select.Option key={opt.id} value={opt.id}>
                                                {`${opt.first_name || ""} ${opt.last_name || ""}`}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <FieldLabel required>Letter Provided Date</FieldLabel>
                                    <DatePicker
                                        value={row.letter_provided_date ? dayjs(row.letter_provided_date) : null}
                                        onChange={(d) => updateLetterRow(index, "letter_provided_date", d)}
                                        format="DD-MM-YYYY"
                                        className="w-full"
                                        disabled={isProcessFinished}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <FieldLabel required>Letter Status</FieldLabel>
                                    <Select
                                        value={row.letter_status || undefined}
                                        onChange={(v) => updateLetterRow(index, "letter_status", v)}
                                        placeholder="Select status"
                                        className="w-full"
                                        disabled={isProcessFinished}
                                    >
                                        {LETTER_STATUS.map((opt) => (
                                            <Select.Option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex items-end pb-1">
                                    <Checkbox
                                        checked={row.is_send_mail}
                                        onChange={(e) => updateLetterRow(index, "is_send_mail", e.target.checked)}
                                        disabled={isProcessFinished}
                                    >
                                        Send Mail
                                    </Checkbox>
                                </div>
                            </div>
                            <div className="mt-3 space-y-1">
                                <FieldLabel required>Document Comments</FieldLabel>
                                <Textarea
                                    value={row.document_comments}
                                    onChange={(e) => updateLetterRow(index, "document_comments", e.target.value)}
                                    rows={2}
                                    showCharCount
                                    maxLength={150}
                                    disabled={isProcessFinished}
                                />
                            </div>
                            <div className="mt-3 space-y-1">
                                <FieldLabel>Upload Document (PDF / Word)</FieldLabel>
                                <Upload.Dragger
                                    accept=".pdf,.doc,.docx"
                                    multiple={false}
                                    maxCount={1}
                                    beforeUpload={() => false}
                                    fileList={row.uploaded_documents?.filter((f) => f && (f.uid || f.originFileObj)) || []}
                                    onChange={({ fileList }) => updateLetterRow(index, "uploaded_documents", fileList)}
                                    showUploadList={false}
                                    disabled={isProcessFinished}
                                >
                                    <UploadCloud className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                        Click or drag to upload (PDF / Word)
                                    </p>
                                </Upload.Dragger>
                                {fileName && (
                                    <div className="mt-2 p-2 flex justify-between items-center border border-gray-200 dark:border-gray-700 rounded">
                                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{fileName}</span>
                                        {fileUrl && (
                                            <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 dark:text-blue-400 text-xs flex items-center gap-1"
                                            >
                                                <Download className="w-3 h-3" /> Download
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={addLetterRow}
                    disabled={isProcessFinished}
                    className="flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> Add Document
                </Button>
            </div>

            <SectionTitle>Final Settlement</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <FieldLabel required>Final Settlement Date</FieldLabel>
                    <DatePicker
                        value={finalForm.final_settlement_date}
                        disabled
                        format="DD-MM-YYYY"
                        className="w-full"
                        size="large"
                    />
                </div>
                <div className="space-y-2">
                    <FieldLabel required>Final Settlement Status</FieldLabel>
                    <Select
                        value={finalForm.final_settlement_status}
                        onChange={(v) => setFinalForm((p) => ({ ...p, final_settlement_status: v }))}
                        placeholder="Select status"
                        className="w-full"
                        size="large"
                        disabled={isProcessFinished}
                    >
                        {SETTLEMENT_STATUS.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <FieldLabel required>Final Settlement Comments</FieldLabel>
                <Textarea
                    value={finalForm.final_settlement_comments}
                    onChange={(e) => setFinalForm((p) => ({ ...p, final_settlement_comments: e.target.value }))}
                    rows={3}
                    showCharCount
                    maxLength={150}
                    disabled={isProcessFinished}
                />
            </div>
        </div>
    );

    if (screenLoader) {
        return (
            <ContainerCard>
                <div className="h-[60vh] flex items-center justify-center">
                    <Spin />
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            {/* Stepper Header */}
            <div className="mb-4 shrink-0">
                <div className="flex items-center justify-between relative">
                    <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700 -z-10">
                        <div
                            className="h-full bg-sidebar-primary transition-all duration-300"
                            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                        />
                    </div>
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        const isCompleted = index < currentStep;
                        const isActive = index === currentStep;
                        const showEmployee = isCompleted && index === 0 && empCardDetails;

                        return (
                            <div key={index} className="flex flex-col items-center relative">
                                {showEmployee ? (
                                    <div
                                        onClick={() => setCurrentStep(index)}
                                        className="min-w-[120px] cursor-pointer px-2"
                                    >
                                        <EmployeeDetails details={empCardDetails} size="xs" />
                                    </div>
                                ) : (
                                    <div
                                        className={cn(
                                            "flex flex-col lg:flex-row items-center justify-center gap-2 sm:gap-3 min-w-[100px] lg:min-w-[140px] px-2 cursor-pointer"
                                        )}
                                        onClick={() => setCurrentStep(index)}
                                    >
                                        <button
                                            className={cn(
                                                "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2 flex-shrink-0",
                                                isCompleted && "bg-sidebar-primary border-sidebar-primary text-white",
                                                isActive && "border-sidebar-primary bg-white dark:bg-gray-800 text-sidebar-primary",
                                                !isActive && !isCompleted && "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-400"
                                            )}
                                        >
                                            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </button>
                                        <p
                                            className={cn(
                                                "text-xs sm:text-sm font-medium whitespace-nowrap text-center sm:text-left",
                                                isActive ? "text-sidebar-primary dark:text-sidebar-primary" : "text-gray-500 dark:text-gray-400"
                                            )}
                                        >
                                            {step.title}
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Step Content */}
            <div className="flex-1 min-h-0 overflow-y-auto mb-2">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    {currentStep === 0 && renderSubmission()}
                    {currentStep === 1 && renderAttendance()}
                    {currentStep === 2 && renderAudit()}
                    {currentStep === 3 && renderAssetsHandover()}
                    {currentStep === 4 && renderFinalStep()}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center shrink-0 pt-2 border-t border-border">
                <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={saving}
                    className="flex items-center gap-2"
                >
                    <ChevronLeft className="w-4 h-4" />
                    {currentStep === 0 ? "Back" : "Previous"}
                </Button>

                {currentStep === steps.length - 1 ? (
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            onClick={() => saveFinalStep({ sendMail: false, finishProcess: false })}
                            disabled={saving || isProcessFinished}
                            className="flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" /> Save
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => saveFinalStep({ sendMail: true, finishProcess: false })}
                            disabled={saving || isProcessFinished}
                            className="flex items-center gap-2"
                        >
                            <Mail className="w-4 h-4" /> Send Mail
                        </Button>
                        <Button
                            onClick={() => saveFinalStep({ sendMail: false, finishProcess: true })}
                            disabled={saving || isProcessFinished || (!isAbscond && finalForm.final_settlement_status !== "cleared")}
                            variant="brand"
                            className="flex items-center gap-2"
                        >
                            <CheckCircle2 className="w-4 h-4" /> Finish Process
                        </Button>
                        {isAbscond && !isProcessFinished && (
                            <Button
                                variant="outline"
                                onClick={() => setCancelOpen(true)}
                                disabled={saving}
                                className="flex items-center gap-2 text-red-600 border-red-200"
                            >
                                Cancel case
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            Step {currentStep + 1} of {steps.length}
                        </span>
                        <Button
                            onClick={handleNext}
                            disabled={saving}
                            variant="brand"
                            className="flex items-center gap-2"
                        >
                            Next <ChevronRight className="w-4 h-4" />
                        </Button>
                    </>
                )}
            </div>
            <DialogModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                onSave={cancelAbscondCase}
                title="Cancel abscond case"
                saveLabel="Cancel case"
                width="520px"
                loading={cancelling}
            >
                <div className="space-y-3 p-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        This restores the employee&apos;s active status, login and payroll hold.
                    </p>
                    <label className={modalFieldLabelClass}>
                        Reason <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={3}
                        placeholder="Why is this abscond case being cancelled?"
                    />
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default ResignationProcess;
